import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useView } from '../context/ViewContext';

const ProductionSummary = () => {
    const navigate = useNavigate();
    const { isOwnerView } = useView();
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [productionData, setProductionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeBatch, setActiveBatch] = useState(0);

    const fetchProductionSummary = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const userStr = localStorage.getItem('user');
            if (!userStr) {
                navigate('/login');
                return;
            }
            const user = JSON.parse(userStr);

            const response = await axios.get(
                `https://rotica.in/get_production_summary.php?date=${selectedDate}&user_id=${user.id}&role=${isOwnerView ? 'owner' : user.role}`
            );

            if (response.data.success) {
                setProductionData(response.data);
            } else {
                setError(response.data.message || "Failed to fetch production data.");
            }
        } catch (err) {
            console.error(err);
            setError("Network Error: Could not fetch production data.");
        } finally {
            setLoading(false);
        }
    }, [selectedDate, navigate, isOwnerView]);

    useEffect(() => {
        fetchProductionSummary();
    }, [fetchProductionSummary]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    const navigateDate = (direction) => {
        const current = new Date(selectedDate);
        current.setDate(current.getDate() + direction);
        setSelectedDate(current.toISOString().split('T')[0]);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-IN', options);
    };

    const isToday = selectedDate === new Date().toISOString().split('T')[0];

    const getDisplayProducts = () => {
        if (!productionData) return [];
        if (activeBatch === 0) return productionData.total_products;
        const batch = productionData.batches.find((b, i) => i + 1 === activeBatch);
        return batch ? batch.products : [];
    };

    return (
        <div className="production-summary-page">
            <header className="production-header">
                <h1 className="page-title">Production Summary</h1>
                <p className="page-subtitle">Daily requirements by batch</p>
            </header>

            {/* Combined Dashboard Panel: Date & Stats */}
            <div className="production-dashboard-panel">
                <div className="dashboard-row">
                    {/* Date Navigator */}
                    <div className="date-navigator-minimal">
                        <button className="nav-btn" onClick={() => navigateDate(-1)}>&larr;</button>
                        <div className="current-date-group">
                            <span className="current-date-lg">{new Date(selectedDate).getDate()}</span>
                            <div className="date-details">
                                <span className="current-month">{new Date(selectedDate).toLocaleDateString('en-US', { month: 'short' })}</span>
                                <span className="current-year">{new Date(selectedDate).getFullYear()}</span>
                            </div>
                        </div>
                        <button className="nav-btn" onClick={() => navigateDate(1)}>&rarr;</button>
                        {isToday && <span className="badge-today">Today</span>}
                    </div>

                    {/* Compact Metrics */}
                    {productionData && !loading && (
                        <div className="dashboard-metrics">
                            <div className="metric-box">
                                <span className="metric-label">Total Items</span>
                                <span className="metric-value">{productionData.total_quantity || 0}</span>
                            </div>
                            <div className="metric-box">
                                <span className="metric-label">Unique Products</span>
                                <span className="metric-value">{productionData.product_count || 0}</span>
                            </div>
                            <div className="metric-box highlight">
                                <span className="metric-label">Batch 1</span>
                                <span className="metric-value">
                                    {productionData.batches[0]?.products.reduce((s, p) => s + p.quantity, 0) || 0}
                                </span>
                            </div>
                            <div className="metric-box highlight">
                                <span className="metric-label">Batch 2</span>
                                <span className="metric-value">
                                    {productionData.batches[1]?.products.reduce((s, p) => s + p.quantity, 0) || 0}
                                </span>
                            </div>
                            <div className="metric-box highlight">
                                <span className="metric-label">Batch 3</span>
                                <span className="metric-value">
                                    {productionData.batches[2]?.products.reduce((s, p) => s + p.quantity, 0) || 0}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Batch Tabs */}
            <div className="batch-control-bar">
                <div className="segmented-control">
                    {[
                        { id: 0, label: 'All Batches', time: '' },
                        { id: 1, label: 'Batch 1', time: '7AM - 10AM' },
                        { id: 2, label: 'Batch 2', time: '12PM - 3PM' },
                        { id: 3, label: 'Batch 3', time: '6PM - 9PM' }
                    ].map(batch => (
                        <button
                            key={batch.id}
                            className={`segment-btn ${activeBatch === batch.id ? 'active' : ''}`}
                            onClick={() => setActiveBatch(batch.id)}
                        >
                            <span className="segment-label">{batch.label}</span>
                            {batch.time && <span className="segment-time">{batch.time}</span>}
                        </button>
                    ))}
                </div>
            </div>

            {/* Data Table View */}
            <div className="production-data-container">
                {loading && <div className="loading-container">Loading data...</div>}

                {error && (
                    <div className="error-container">
                        <p>{error}</p>
                        <button onClick={fetchProductionSummary}>Retry</button>
                    </div>
                )}

                {!loading && !error && productionData && (
                    <div className="table-card">
                        {getDisplayProducts().length > 0 ? (
                            <table className="production-table">
                                <thead>
                                    <tr>
                                        <th>Product Name</th>
                                        <th style={{ textAlign: 'center' }}>Units Required</th>
                                        <th style={{ textAlign: 'center' }}>Pack Of</th>
                                        <th style={{ textAlign: 'center' }}>Status</th>
                                        <th style={{ textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getDisplayProducts().map((product, index) => (
                                        <tr key={product.id}>
                                            <td className="product-cell">
                                                <div className="product-name-wrapper">
                                                    <span className="p-name">{product.name}</span>
                                                    <span className="p-base">{product.base_name}</span>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className="qty-badge">{product.quantity || 0}</span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className="pack-badge">{product.pack_size || 1}</span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className="status-dot pending">Pending</span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button
                                                    className="action-icon-btn"
                                                    onClick={() => navigate('/inventory')}
                                                >
                                                    Check Stock
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-table-state">
                                <p>No production requirements for this selection.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Owner's Birds-Eye View: Individual Assignments */}
            {isOwnerView && productionData && productionData.assigned_orders && (
                <div className="owner-birds-eye-section animated fadeIn">
                    <header className="section-header">
                        <h2>Assigned Orders Overview</h2>
                        <p>Track where each order is currently assigned</p>
                    </header>

                    <div className="table-card">
                        <table className="production-table assigned-orders-table">
                            <thead>
                                <tr>
                                    <th>Order</th>
                                    <th>Customer</th>
                                    <th>Delivery Time</th>
                                    <th>Assigned To</th>
                                    <th>Entity Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productionData.assigned_orders.length > 0 ? (
                                    productionData.assigned_orders.map(order => (
                                        <tr key={order.id}>
                                            <td><strong>#{order.id}</strong></td>
                                            <td>{order.customer_name}</td>
                                            <td>
                                                <span className="time-badge">{order.delivery_time}</span>
                                            </td>
                                            <td>
                                                <div className="entity-cell">
                                                    <span className="entity-name">{order.assigned_name}</span>
                                                    <span className="entity-id">ID: #{order.assigned_id}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`type-badge ${order.assigned_type}`}>
                                                    {order.assigned_type?.charAt(0).toUpperCase() + order.assigned_type?.slice(1)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                                            No orders found for this date.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductionSummary;
